import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import type { WorkListProps } from "./types";
import { RowActions } from "./RowActions";
import styles from "./ToolPage.module.css";
import { useToolEvents } from "./ToolEvents";
export function WorkList({
  items,
  state,
  emptyContent,
  errorContent,
  pagination,
  onOpen,
}: WorkListProps) {
  const track = useToolEvents();
  if (state === "loading")
    return (
      <p role="status" className={styles.empty}>
        Loading…
      </p>
    );
  const errorNotice =
    state === "error" ? (
      <div role="alert" className={styles.empty}>
        {errorContent || "Could not load this collection. Please try again."}
        {items.length > 0 && (
          <p>Showing previously loaded items. They may be out of date.</p>
        )}
      </div>
    ) : null;
  if (state === "error" && !items.length) return errorNotice;
  if (!items.length) return <div className={styles.empty}>{emptyContent}</div>;
  return (
    <>
      {errorNotice}
      <ul className={styles.list}>
        {items.map((item) => (
          <li
            key={item.id}
            id={`tool-row-${item.id}`}
            className={styles.row}
            data-testid={`work-item-${item.id}`}
          >
            <Link
              to={item.href}
              className={styles.open}
              data-testid={item.testId}
              onClick={() => {
                track?.("work_opened");
                onOpen?.(item.id);
              }}
            >
              <div className={styles.identity}>
                <h3>{item.title}</h3>
                {item.description && <p>{item.description}</p>}
              </div>
              <div className={styles.metadata}>
                {item.status && (
                  <span
                    className={`${styles.status} ${styles[item.status.tone]}`}
                  >
                    {item.status.label}
                  </span>
                )}
                {item.metadata.map((m) => (
                  <span key={m.label}>
                    {m.label} {m.value}
                  </span>
                ))}
              </div>
              <ChevronRight
                size={18}
                className={styles.chevron}
                aria-hidden="true"
              />
            </Link>
            <RowActions label={item.title} actions={item.actions} />
          </li>
        ))}
      </ul>
      {pagination && pagination.pageCount > 1 && (
        <nav aria-label="Collection pages" className={styles.pagination}>
          <button
            type="button"
            disabled={pagination.page <= 1}
            onClick={() => pagination.onChange(pagination.page - 1)}
          >
            Previous
          </button>
          <span>
            Page {pagination.page} of {pagination.pageCount}
          </span>
          <button
            type="button"
            disabled={pagination.page >= pagination.pageCount}
            onClick={() => pagination.onChange(pagination.page + 1)}
          >
            Next
          </button>
        </nav>
      )}
    </>
  );
}
